import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';

import { generateUUID, getClassNames, retrieveFromLocalStorage, isURL } from '../../../utils/util';
import { LONG_MONTHS, NOTIFICATION_CONTEXT_TYPES } from '../../../utils/constants';

import {
  requestViewNotifications,
  requestAcknowledgeNotification,
} from '../../../api/notification/notification.actions';
import { requestRetrieveShortcuts } from '../../../api/system-user/system-user.actions';

import Dropdown from '../inputs/dropdown/Dropdown';

import './notification-panel.scss';

const NotificationPanel = ({ showModal, setAreNewNotifications, setNewNotificationsSize }) => {
  const notificationDropdownValues = [
    { label: 'All Notifications', value: 'All Notifications' },
    { label: 'Bookmark Updated', value: 'Bookmark Updated' },
    { label: 'Communication', value: 'Communication' },
    { label: 'Deal Room', value: 'Deal Room' },
    { label: 'Listing Management', value: 'Listing Management' },
  ];
  const { notifications, userGroups } = useSelector(({ party }) => party);
  const [filteredUnreadNotifications, setFilteredUnreadNotifications] = useState([]);
  const [filteredReadNotifications, setFilteredReadNotifications] = useState([]);
  const [dropDownValue, setDropDownValue] = useState(notificationDropdownValues[0]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const personExternalReference = retrieveFromLocalStorage('personExternalReference');

  useEffect(() => {
    const receiverExternalReference = retrieveFromLocalStorage('personExternalReference');
    dispatch(requestViewNotifications({ receiverExternalReference }));
  }, [dispatch, showModal]);

  useEffect(() => {
    let readArray = [];
    let unreadArray = [];

    notifications.forEach((notification) => {
      if (notification.acknowledged) {
        readArray.push(notification);
      } else {
        unreadArray.push(notification);
      }
    });

    setFilteredUnreadNotifications(unreadArray);
    setAreNewNotifications(unreadArray.length > 0);
    setNewNotificationsSize(unreadArray.length);
    setFilteredReadNotifications(readArray);
  }, [dispatch, notifications, setAreNewNotifications, setNewNotificationsSize]);

  const handleNotificationTypeChange = (option) => {
    setDropDownValue(option);
    filterReadNotifications(option);
    filterUnreadNotifications(option);
  };

  const handleNotificationClick = (notification) => {
    if (!notification.acknowledged) {
      dispatch(
        requestAcknowledgeNotification({
          externalReference: notification.externalReference,
          onSuccess: () => {
            dispatch(requestViewNotifications({ receiverExternalReference: personExternalReference }));

            if (notification.notificationContext === NOTIFICATION_CONTEXT_TYPES[1].label) {
              dispatch(requestRetrieveShortcuts());
            }
          },
        })
      );
    }

    determineNotificationNavigation(notification);
  };

  const determineNotificationNavigation = (notification) => {
    switch (notification.notificationContext) {
      case NOTIFICATION_CONTEXT_TYPES[0].label:
        navigate(`/shop/deal-room/${notification.contextReference}`);
        break;
      case NOTIFICATION_CONTEXT_TYPES[1].label:
        navigate(`market/direct-messages/${notification.contextReference}`);
        break;
      case NOTIFICATION_CONTEXT_TYPES[2].label:
        navigate(`/shop/deal-room/${notification.contextReference}/open`);
        break;
      case NOTIFICATION_CONTEXT_TYPES[3].label:
        navigate('/verification');
        break;
      case NOTIFICATION_CONTEXT_TYPES[4].label:
        navigate(`/shop/deal-room/${notification.contextReference}/open`);
        break;
      case NOTIFICATION_CONTEXT_TYPES[5].label:
        navigate(`/shop/deal-room/${notification.contextReference}`);
        break;
      case NOTIFICATION_CONTEXT_TYPES[6].label:
        navigate(`/shop/deal-room/${notification.contextReference}`);
        break;
      case NOTIFICATION_CONTEXT_TYPES[7].label:
        navigate(`/shop/deal-room/${notification.contextReference}`);
        break;
      case NOTIFICATION_CONTEXT_TYPES[8].label:
        navigate(`/shop/upload-product-listing/${notification.contextReference}`);
        break;
      case NOTIFICATION_CONTEXT_TYPES[9].label:
        navigate(`/market/product-listing/${notification.contextReference}`);
        break;
      case NOTIFICATION_CONTEXT_TYPES[10].label:
        navigate(`/shop/upload-buyer-listing/${notification.contextReference}`);
        break;
      case NOTIFICATION_CONTEXT_TYPES[11].label:
        navigate(`/market/buyer-listing/${notification.contextReference}`);
        break;
      case NOTIFICATION_CONTEXT_TYPES[12].label:
        navigate(`/admin/product-specifications/${notification.contextReference}`);
        break;
      case NOTIFICATION_CONTEXT_TYPES[13].label:
        navigate(`/admin/product-management/product-listing/${notification.contextReference}`);
        break;
      case NOTIFICATION_CONTEXT_TYPES[14].label:
        navigate(`/admin/product-management/buyer-listing/${notification.contextReference}`);
        break;
      case NOTIFICATION_CONTEXT_TYPES[15].label:
        navigate('/admin/product-management');
        break;
      case NOTIFICATION_CONTEXT_TYPES[16].label:
        if (userGroups.includes('admin') || userGroups.includes('facilitator')) {
          navigate(`/admin/support/${notification.contextReference}`);
        } else {
          navigate('/help-center/contact-us');
        }
        break;
      default:
        return;
    }
  };

  const filterUnreadNotifications = (option) => {
    if (option.value === 'All Notifications') {
      setFilteredUnreadNotifications(notifications);
      return;
    }

    const filteredNotifications = notifications.filter((notification) => {
      return notification.filterType === option.value;
    });

    setFilteredUnreadNotifications(filteredNotifications);
  };

  const filterReadNotifications = (option) => {
    if (option.value === 'All Notifications') {
      setFilteredReadNotifications(notifications);
      return;
    }

    const filteredNotifications = notifications.filter((notification) => {
      return notification.filterType === option.value;
    });

    setFilteredReadNotifications(filteredNotifications);
  };

  const formatDate = (date) => {
    if (!date) return;
    const dateObject = new Date(date);
    const day = dateObject.getDate();
    let hours = dateObject.getHours() - 2;
    let minutes = dateObject.getMinutes();

    if (hours < 10) {
      hours = `0${hours}`;
    }

    if (minutes < 10) {
      minutes = `0${minutes}`;
    }

    const currentDate = new Date();
    if (day === currentDate.getDate()) {
      return `Today at ${hours}:${minutes}`;
    }

    if (day === currentDate.getDate() - 1) {
      return `Yesterday at ${hours}:${minutes}`;
    }

    const month = LONG_MONTHS[dateObject.getMonth()];

    return `${day} ${month} at ${hours}:${minutes}`;
  };

  const renderText = (text) => {
    const paragraphs = text.split('\n');
    return paragraphs.map((paragraph, index) => {
      if (paragraph === '') {
        return <br key={index} />;
      }

      const splitText = paragraph.split(' ');
      const textWithLinks = splitText.map((text, index) => {
        if (isURL(text)) {
          return (
            <Link key={index} className="notifications__link" target="_blank" to={text}>
              {text}
            </Link>
          );
        }

        if (index === splitText.length - 1) {
          return text;
        } else {
          return text + ' ';
        }
      });

      return <p key={index}>{textWithLinks}</p>;
    });
  };

  const renderNotifications = () => {
    return filteredUnreadNotifications.map((notification) => {
      const { message, creationDate } = notification;
      return (
        <div
          className="notifications__notification"
          onClick={() => handleNotificationClick(notification)}
          key={generateUUID()}
        >
          <div className="notifications__notification__content">
            <p className="notifications__notification__content__line-one">{renderText(message)}</p>
            <p className="notifications__notification__content__line-two">{formatDate(creationDate)}</p>
          </div>
        </div>
      );
    });
  };

  const renderReadNotifications = () => {
    return filteredReadNotifications.map((notification) => {
      const { message, creationDate } = notification;
      return (
        <div
          className="notifications__unread-notification"
          onClick={() => handleNotificationClick(notification)}
          key={generateUUID()}
        >
          <div className="notifications__unread-notification__content">
            <p className="notifications__unread-notification__content__line-one">{renderText(message)}</p>
            <p className="notifications__unread-notification__content__line-two">{formatDate(creationDate)}</p>
          </div>
        </div>
      );
    });
  };

  return (
    <div className={getClassNames('notifications', { show: showModal })} data-testid="notifications">
      <div className="notifications__dropdown">
        <Dropdown
          name="notification-types"
          value={{ label: dropDownValue.label, value: dropDownValue.value }}
          options={notificationDropdownValues}
          onChange={(option) => handleNotificationTypeChange(option)}
        />
      </div>

      <div>
        <p className="notifications__notification__heading">Unread Notifications:</p>
      </div>
      <div>
        {filteredUnreadNotifications && filteredUnreadNotifications.length > 0 && renderNotifications()}
        {(!filteredUnreadNotifications || filteredUnreadNotifications.length === 0) && (
          <div className="notifications__no-notifications">No new notifications</div>
        )}
      </div>

      <div>
        <p className="notifications__notification__heading">Read Notifications:</p>
      </div>
      <div>
        {filteredReadNotifications && filteredReadNotifications.length > 0 && renderReadNotifications()}
        {(!filteredReadNotifications || filteredReadNotifications.length === 0) && (
          <div className="notifications__no-notifications">No unread notifications</div>
        )}
      </div>
    </div>
  );
};

NotificationPanel.propTypes = {
  showModal: PropTypes.bool,
};

export default NotificationPanel;
